import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Alert,
} from '@mui/material';
import {
  Person as PersonIcon,
  MenuBook as MenuBookIcon,
  LocalOffer as LocalOfferIcon,
  Receipt as ReceiptIcon,
  ShoppingCart,
  ArrowBack,
  Extension as ExtensionIcon,
} from '@mui/icons-material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { financeAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import StatusBadge from '../../components/Finance/StatusBadge';
import { formatCurrency } from '../../components/Finance/formatCurrency';
import RazorpayInvoicePanel from '../../components/Finance/RazorpayInvoicePanel';

const PLAN_LABELS = { monthly: 'Monthly', quarterly: 'Quarterly', six_months: '6 Months', yearly: 'Yearly' };
const BATCH_LABELS = { one_on_one: 'One on One', group: 'Group' };
const isTruthyBoolean = (value) =>
  value === true || value === 1 || value === '1' || value === 'true' || value === 't';

/** Readable date for invoice cycle labels, e.g. "26 Jul 2026". */
function formatReadableDate(value) {
  if (!value) return null;
  const s = String(value).slice(0, 10);
  const d = new Date(`${s}T00:00:00`);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Invoice billing window, e.g. "From 26 Jul 2026 to 22 Aug 2026". */
function formatInvoiceCycleLabel(inv) {
  if (!inv) return '—';
  const start = formatReadableDate(inv.cycle_start);
  const end = formatReadableDate(inv.cycle_end);
  if (start && end) return `From ${start} to ${end}`;
  if (start) return `From ${start}`;
  if (inv.cycle_month) return String(inv.cycle_month);
  return inv.id || '—';
}

function getWeeksForSubscriptionModel(model) {
  const key = String(model || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
  switch (key) {
    case 'monthly':
      return 4;
    case 'quarterly':
      return 12;
    case 'six_months':
    case '6_months':
    case 'sixmonths':
    case 'half_yearly':
      return 24;
    case 'yearly':
      return 48;
    default:
      return 4;
  }
}

const SubscriptionDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { isFinanceOrAdmin, isAdmin } = useAuth();
  const isAdmissionView = location.pathname.startsWith('/admission');
  const canRecordPayment = isFinanceOrAdmin() || isAdmin();
  const listPath = isAdmissionView ? '/admission' : '/finance';

  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: '', currency: '', dueDate: '', paidAt: '', paymentMethod: 'manual', manualReferenceId: '', notes: '',
    invoiceId: '',
  });
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    status: 'active',
    subscriptionModel: 'monthly',
    batchType: 'one_on_one',
    serviceStartDate: '',
    enrollmentDate: '',
    classesPerWeek: 2,
    bonusClasses: 0,
    openInvoiceAction: 'keep',
    reason: '',
  });
  const [effectiveDialogOpen, setEffectiveDialogOpen] = useState(false);
  const [pendingEffectiveAction, setPendingEffectiveAction] = useState(null);
  const [effectiveMode, setEffectiveMode] = useState('next'); // 'next' | 'previous'
  const [effectiveInvoiceId, setEffectiveInvoiceId] = useState('');
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [discountForm, setDiscountForm] = useState({
    discountType: 'sibling',
    value: '',
    isPercentage: true,
    duration: 'one_time',
    notes: '',
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteOpenInvoiceAction, setDeleteOpenInvoiceAction] = useState('keep');
  const [addOnDialogOpen, setAddOnDialogOpen] = useState(false);
  const [addOnForm, setAddOnForm] = useState({
    addOnType: '',
    quantity: 1,
    invoiceId: '',
  });

  const subscriptionQuery = useQuery(
    ['financeSubscription', id],
    async () => {
      const res = await financeAPI.getSubscription(id);
      return res.data;
    },
    { enabled: !!id }
  );

  const sub = subscriptionQuery.data?.data || null;
  const isLoading = subscriptionQuery.isLoading;

  useEffect(() => {
    if (!sub) return;
    const state = location.state || {};
    if (state.openPayment && canRecordPayment) {
      setPaymentForm({
        amount: '', currency: sub.currency || '', dueDate: sub.next_due_date || '', paidAt: '',
        paymentMethod: 'manual', manualReferenceId: '', notes: '',
        invoiceId: '',
      });
      setPaymentDialogOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    } else if (state.openEdit) {
      setEditForm({
        status: sub.status || 'active',
        subscriptionModel: sub.subscription_model || 'monthly',
        batchType: sub.batch_type || 'one_on_one',
        serviceStartDate: sub.service_start_date || '',
        enrollmentDate: sub.enrollment_date || '',
        classesPerWeek: sub.classes_per_week ?? 2,
        bonusClasses: sub.bonus_classes ?? 0,
        openInvoiceAction: 'keep',
        reason: '',
      });
      setEditDialogOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [sub, location.state, location.pathname, navigate, canRecordPayment]);

  const invoicesQuery = useQuery(
    ['financeSubInvoices', id],
    async () => {
      const res = await financeAPI.getInvoicesForSubscription(id);
      return res.data;
    },
    { enabled: !!id }
  );
  const paymentsQuery = useQuery(
    ['financeSubPayments', id],
    async () => {
      const res = await financeAPI.getPaymentsForSubscription(id);
      return res.data;
    },
    { enabled: !!id }
  );
  const discountsQuery = useQuery(
    ['financeSubDiscounts', id],
    async () => {
      const res = await financeAPI.getDiscountsForSubscription(id);
      return res.data;
    },
    { enabled: !!id }
  );

  const discountCatalogQuery = useQuery(['financeDiscountCatalog'], async () => {
    const res = await financeAPI.getDiscountCatalog();
    return res.data;
  });

  const addOnsQuery = useQuery(
    ['financeSubAddOns', id],
    async () => {
      const res = await financeAPI.getAddOnsForSubscription(id);
      return res.data;
    },
    { enabled: !!id }
  );

  const addOnCatalogQuery = useQuery(['financeAddOnCatalog'], async () => {
    const res = await financeAPI.getAddOnCatalog();
    return res.data;
  });

  const cycleStart = sub?.service_start_date || '';
  const cycleEnd = sub?.next_due_date
    ? new Date(new Date(sub.next_due_date).getTime() - 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
    : '';
  useQuery(
    ['financeBillingSummary', id, cycleStart, cycleEnd],
    async () => {
      const res = await financeAPI.getBillingSummary(id, { cycleStart, cycleEnd });
      return res.data;
    },
    { enabled: !!id && !!cycleStart && !!cycleEnd }
  );

  const invoices = useMemo(() => invoicesQuery.data?.data || [], [invoicesQuery.data]);
  const discounts = useMemo(() => discountsQuery.data?.data || [], [discountsQuery.data]);
  const payments = useMemo(() => paymentsQuery.data?.data || [], [paymentsQuery.data]);
  const addOns = useMemo(() => addOnsQuery.data?.data || [], [addOnsQuery.data]);
  const addOnCatalog = addOnCatalogQuery.data?.data || [];

  const changeHistoryQuery = useQuery(
    ['financeSubChangeHistory', id],
    async () => {
      const res = await financeAPI.getSubscriptionChangeHistory(id);
      return res.data;
    },
    { enabled: !!id }
  );
  const changeHistory = changeHistoryQuery.data?.data || [];
  const discountCatalog = discountCatalogQuery.data?.data || [];
  const activeDiscountTypes = discountCatalog.filter((d) => d.isActive !== false);
  const discountTypeDefaults = new Map(discountCatalog.map((d) => [d.type, !!d.defaultIsPercentage]));
  const discountTypeDefaultValues = new Map(discountCatalog.map((d) => [d.type, d.defaultValue ?? null]));
  const discountTypeValueLocks = new Map(discountCatalog.map((d) => [d.type, !!d.isValueLocked]));
  const isSelectedDiscountTypeLocked = !!discountTypeValueLocks.get(discountForm.discountType);

  const studentName = sub
    ? (sub.student_first_name || sub.student_last_name
      ? `${sub.student_first_name || ''} ${sub.student_last_name || ''}`.trim()
      : (sub.student_display_id || sub.student_id || '').toString())
    : '';

  const outstandingInvoices = useMemo(
    () => invoices.filter((i) => ['open', 'partially_paid'].includes(String(i.status))),
    [invoices]
  );

  // Gross invoice totals for still-outstanding cycles only (exclude paid/closed invoices).
  const outstandingInvoiceGross = useMemo(() => {
    return outstandingInvoices.reduce((s, i) => s + (Number(i.amount_due) || 0), 0);
  }, [outstandingInvoices]);

  const outstandingInvoiceRemaining = useMemo(() => {
    return outstandingInvoices.reduce((s, i) => {
      const due = Number(i.amount_due) || 0;
      const paid = Number(i.amount_paid) || 0;
      return s + Math.max(0, due - paid);
    }, 0);
  }, [outstandingInvoices]);

  // Discounts tied to an invoice (invoice_id set) are already reflected in that invoice's amount_due after apply-from-invoice flows.
  // Only subscription-level rows (no invoice_id) still need to adjust the payable total.
  const uninvoicedDiscountTotal = useMemo(() => {
    return discounts
      .filter((d) => !(d.invoice_id || d.invoiceId))
      .reduce((s, d) => {
        const v = Number(d.value) || 0;
        const isPercentage = isTruthyBoolean(d.is_percentage ?? d.isPercentage);
        return s + (isPercentage ? (outstandingInvoiceGross * v) / 100 : v);
      }, 0);
  }, [discounts, outstandingInvoiceGross]);

  const hasInvoicedDiscounts = useMemo(
    () => discounts.some((d) => !!(d.invoice_id || d.invoiceId)),
    [discounts]
  );

  const uninvoicedAddOnTotal = useMemo(() => {
    return addOns
      .filter((a) => !(a.invoice_id || a.invoiceId))
      .reduce((s, a) => s + (Number(a.unit_price) || 0) * (Number(a.quantity) || 0), 0);
  }, [addOns]);

  const hasInvoicedAddOns = useMemo(
    () => addOns.some((a) => !!(a.invoice_id || a.invoiceId)),
    [addOns]
  );

  const totalAmountDue = Math.max(
    0,
    outstandingInvoiceRemaining - uninvoicedDiscountTotal + uninvoicedAddOnTotal
  );

  const computedTotalClasses = useMemo(() => {
    if (!sub) return null;
    const weeks = getWeeksForSubscriptionModel(sub.subscription_model);
    const perWeek = Number(sub.classes_per_week) || 0;
    const bonus = Number(sub.bonus_classes) || 0;
    return perWeek * weeks + bonus;
  }, [sub]);

  const recordPaymentMutation = useMutation(
    async (payload) => {
      const res = await financeAPI.recordPayment(id, payload);
      return res.data;
    },
    {
      onSuccess: () => {
        toast.success('Payment recorded');
        queryClient.invalidateQueries(['financeSubPayments', id]);
        queryClient.invalidateQueries(['financeSubInvoices', id]);
        queryClient.invalidateQueries(['financeSubscription', id]);
        queryClient.invalidateQueries('financeSubscriptions');
        setPaymentDialogOpen(false);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Failed to record payment');
      },
    }
  );

  const updateSubscriptionMutation = useMutation(
    async (payload) => {
      const res = await financeAPI.updateSubscription(id, payload);
      return res.data;
    },
    {
      onSuccess: () => {
        toast.success('Subscription updated');
        queryClient.invalidateQueries(['financeSubscription', id]);
        queryClient.invalidateQueries('financeSubscriptions');
        setEditDialogOpen(false);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Failed to update');
      },
    }
  );

  const deleteSubscriptionMutation = useMutation(
    async ({ reason, openInvoiceAction }) => {
      const res = await financeAPI.deleteSubscription(id, { reason, openInvoiceAction });
      return res.data;
    },
    {
      onSuccess: () => {
        toast.success('Subscription deleted');
        queryClient.invalidateQueries(['financeSubscription', id]);
        queryClient.invalidateQueries(['financeSubInvoices', id]);
        queryClient.invalidateQueries('financeSubscriptions');
        setDeleteDialogOpen(false);
        navigate(listPath);
      },
      onError: (err) => {
        const msg =
          err.response?.data?.message ||
          (Array.isArray(err.response?.data?.errors)
            ? err.response.data.errors.join(', ')
            : 'Failed to delete subscription');
        toast.error(msg);
      },
    }
  );

  const discountMutation = useMutation(
    async ({ mode, discountId, payload }) => {
      if (mode === 'create') {
        const res = await financeAPI.createSubscriptionDiscount(id, payload);
        return res.data;
      }
      const res = await financeAPI.updateSubscriptionDiscount(id, discountId, payload);
      return res.data;
    },
    {
      onSuccess: () => {
        toast.success('Discount saved');
        queryClient.invalidateQueries(['financeSubDiscounts', id]);
        setDiscountDialogOpen(false);
        setEditingDiscount(null);
      },
      onError: (err) => {
        const msg =
          err.response?.data?.message ||
          (Array.isArray(err.response?.data?.errors) ? err.response.data.errors.join(', ') : 'Failed to save discount');
        toast.error(msg);
      },
    }
  );

  const deleteDiscountMutation = useMutation(
    async ({ discountId, applyFrom, fromInvoiceId }) => {
      const res = await financeAPI.deleteSubscriptionDiscount(id, discountId, {
        applyFrom,
        fromInvoiceId,
      });
      return res.data;
    },
    {
      onSuccess: () => {
        toast.success('Discount deleted');
        queryClient.invalidateQueries(['financeSubDiscounts', id]);
      },
      onError: (err) => {
        const msg =
          err.response?.data?.message ||
          (Array.isArray(err.response?.data?.errors) ? err.response.data.errors.join(', ') : 'Failed to delete discount');
        toast.error(msg);
      },
    }
  );

  const createAddOnMutation = useMutation(
    async (payload) => {
      const res = await financeAPI.createSubscriptionAddOn(id, payload);
      return res.data;
    },
    {
      onSuccess: () => {
        toast.success('Add-on added');
        queryClient.invalidateQueries(['financeSubAddOns', id]);
        setAddOnDialogOpen(false);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Failed to add add-on');
      },
    }
  );

  const deleteAddOnMutation = useMutation(
    async ({ addOnId, applyFrom, fromInvoiceId }) => {
      const res = await financeAPI.deleteSubscriptionAddOn(id, addOnId, {
        applyFrom,
        fromInvoiceId,
      });
      return res.data;
    },
    {
      onSuccess: () => {
        toast.success('Add-on removed');
        queryClient.invalidateQueries(['financeSubAddOns', id]);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Failed to remove add-on');
      },
    }
  );

  const handleOpenPayment = () => {
    setPaymentForm({
      amount: '', currency: sub?.currency || '', dueDate: sub?.next_due_date || '', paidAt: '',
      paymentMethod: 'manual', manualReferenceId: '', notes: '',
      invoiceId: '',
    });
    setPaymentDialogOpen(true);
  };

  const handleSubmitPayment = () => {
    if (!paymentForm.amount || !paymentForm.currency || !paymentForm.dueDate) {
      toast.error('Amount, currency, and due date are required');
      return;
    }
    recordPaymentMutation.mutate({
      invoiceId: paymentForm.invoiceId || null,
      amount: Number(paymentForm.amount),
      currency: paymentForm.currency.toUpperCase(),
      dueDate: paymentForm.dueDate,
      paidAt: paymentForm.paidAt || null,
      paymentMethod: paymentForm.paymentMethod,
      manualReferenceId: paymentForm.manualReferenceId || null,
      notes: paymentForm.notes || null,
    });
  };

  const handleOpenEdit = () => {
    setEditForm({
      status: sub?.status || 'active',
      subscriptionModel: sub?.subscription_model || 'monthly',
      batchType: sub?.batch_type || 'one_on_one',
      serviceStartDate: sub?.service_start_date || '',
      enrollmentDate: sub?.enrollment_date || '',
      classesPerWeek: sub?.classes_per_week ?? 2,
      bonusClasses: sub?.bonus_classes ?? 0,
      openInvoiceAction: 'keep',
      reason: '',
    });
    setEditDialogOpen(true);
  };

  const openInvoices = useMemo(
    () => invoices.filter((inv) => ['open', 'partially_paid'].includes(String(inv.status))),
    [invoices]
  );
  const lastTwoInvoices = useMemo(() => {
    // invoices endpoint returns cycle_start as YYYY-MM-DD; sort desc and take two
    return [...(invoices || [])]
      .sort((a, b) => String(b.cycle_start || '').localeCompare(String(a.cycle_start || '')))
      .slice(0, 2);
  }, [invoices]);
  const handleOpenDelete = () => {
    setDeleteReason('');
    setDeleteOpenInvoiceAction('keep');
    setDeleteDialogOpen(true);
  };
  const handleConfirmDelete = () => {
    deleteSubscriptionMutation.mutate({
      reason: deleteReason,
      openInvoiceAction: openInvoices.length > 0 ? deleteOpenInvoiceAction : undefined,
    });
  };

  const handleSubmitEdit = () => {
    const payload = {
      status: editForm.status,
      subscriptionModel: editForm.subscriptionModel,
      batchType: editForm.batchType,
      serviceStartDate: editForm.serviceStartDate || null,
      enrollmentDate: editForm.enrollmentDate || null,
      classesPerWeek: Number(editForm.classesPerWeek) || 1,
      bonusClasses: Number(editForm.bonusClasses) || 0,
      reason: editForm.reason,
    };
    if (editForm.status === 'cancelled' && openInvoices.length > 0) {
      payload.openInvoiceAction = editForm.openInvoiceAction || 'keep';
    }
    setPendingEffectiveAction({
      kind: 'subscription_update',
      run: ({ applyFrom, fromInvoiceId }) => {
        updateSubscriptionMutation.mutate({
          ...payload,
          applyFrom,
          fromInvoiceId,
        });
      },
    });
    setEffectiveMode('next');
    setEffectiveInvoiceId('');
    setEffectiveDialogOpen(true);
  };

  const handleConfirmEffectiveUpdate = () => {
    if (!pendingEffectiveAction?.run) {
      toast.error('Nothing to update');
      return;
    }
    if (effectiveMode === 'previous' && !effectiveInvoiceId) {
      toast.error('Select an invoice to apply changes from');
      return;
    }
    const applyFrom = effectiveMode === 'previous' ? 'invoice' : 'next';
    const fromInvoiceId = effectiveMode === 'previous' ? effectiveInvoiceId : null;
    pendingEffectiveAction.run({ applyFrom, fromInvoiceId });
    setEffectiveDialogOpen(false);
  };

  const handleDiscountChange = (field, value) => {
    if (field === 'discountType') {
      const defPct = discountTypeDefaults.get(value);
      const lock = discountTypeValueLocks.get(value);
      const defaultValue = discountTypeDefaultValues.get(value);
      setDiscountForm((prev) => ({
        ...prev,
        discountType: value,
        isPercentage: defPct != null ? defPct : prev.isPercentage,
        value: lock ? (defaultValue ?? 0) : prev.value,
      }));
      return;
    }
    if (field === 'isPercentage' && !!discountTypeValueLocks.get(discountForm.discountType)) {
      return;
    }
    setDiscountForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleOpenCreateDiscount = () => {
    const lock = discountTypeValueLocks.get('sibling');
    const defaultValue = discountTypeDefaultValues.get('sibling');
    setEditingDiscount(null);
    setDiscountForm({
      discountType: 'sibling',
      value: lock ? (defaultValue ?? 0) : '',
      isPercentage: true,
      duration: 'one_time',
      notes: '',
    });
    setDiscountDialogOpen(true);
  };

  const handleOpenEditDiscount = (d) => {
    setEditingDiscount(d);
    setDiscountForm({
      discountType: d.discount_type,
      value: d.value,
      isPercentage: isTruthyBoolean(d.is_percentage ?? d.isPercentage),
      duration: d.duration,
      notes: d.notes || '',
    });
    setDiscountDialogOpen(true);
  };

  const handleSubmitDiscount = () => {
    if (discountForm.value === '' || Number(discountForm.value) < 0) {
      toast.error('Discount value must be non-negative');
      return;
    }
    const payload = {
      invoiceId: null,
      discountType: discountForm.discountType,
      value: Number(discountForm.value),
      isPercentage: !!discountForm.isPercentage,
      duration: discountForm.duration,
      notes: discountForm.notes || null,
    };
    const mode = editingDiscount ? 'edit' : 'create';
    const discountId = editingDiscount?.id || null;
    setPendingEffectiveAction({
      kind: `discount_${mode}`,
      run: ({ applyFrom, fromInvoiceId }) => {
        discountMutation.mutate({
          mode,
          discountId,
          payload: {
            ...payload,
            applyFrom,
            fromInvoiceId,
          },
        });
      },
    });
    setEffectiveMode('next');
    setEffectiveInvoiceId('');
    setEffectiveDialogOpen(true);
  };

  const addOnTypeLabel = (key) => addOnCatalog.find((a) => a.key === key)?.label || key;

  const handleOpenAddOn = () => {
    const first = addOnCatalog[0]?.key || '';
    setAddOnForm({ addOnType: first, quantity: 1, invoiceId: '' });
    setAddOnDialogOpen(true);
  };

  const handleSubmitAddOn = () => {
    if (!addOnForm.addOnType) {
      toast.error('Select an add-on type');
      return;
    }
    const q = Number(addOnForm.quantity);
    if (!q || q < 1) {
      toast.error('Quantity must be at least 1');
      return;
    }
    setPendingEffectiveAction({
      kind: 'add_on_create',
      run: ({ applyFrom, fromInvoiceId }) => {
        createAddOnMutation.mutate({
          addOnType: addOnForm.addOnType,
          quantity: q,
          // UI's invoiceId selector remains available, but effective prompt controls how it is applied.
          invoiceId: applyFrom === 'invoice' ? (fromInvoiceId || null) : null,
          applyFrom,
          fromInvoiceId,
        });
      },
    });
    setEffectiveMode('next');
    setEffectiveInvoiceId('');
    setEffectiveDialogOpen(true);
  };

  if (!id) {
    navigate(listPath);
    return null;
  }

  if (subscriptionQuery.isError || (subscriptionQuery.isFetched && !sub)) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Subscription not found.</Typography>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(listPath)} sx={{ mt: 2 }}>
          Back to list
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(listPath)} sx={{ minWidth: 'auto', px: 1 }}>
          Back
        </Button>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" fontWeight="700" gutterBottom>
            {isAdmissionView ? 'Subscription' : 'Fee Configuration'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage enrollment details and billing for {studentName || 'this student'}.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          onClick={async () => {
            try {
              await financeAPI.notifyParentSubscription(id);
              toast.success('Parent notification sent');
            } catch (err) {
              toast.error(err.response?.data?.message || 'Could not notify parent');
            }
          }}
          disabled={!sub || !!sub.deleted_at}
          sx={{ borderRadius: 2 }}
        >
          Notify parent
        </Button>
        {canRecordPayment && (
          <Button
            color="error"
            variant="outlined"
            onClick={handleOpenDelete}
            disabled={!sub || !!sub.deleted_at}
            sx={{ borderRadius: 2 }}
          >
            Delete subscription
          </Button>
        )}
      </Box>

      <Grid container spacing={3}>
        {/* Left column */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <PersonIcon sx={{ color: 'primary.main', fontSize: 28 }} />
              <Typography variant="subtitle1" fontWeight="700">Student Information</Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Full Name"
                  value={studentName || '—'}
                  InputProps={{ readOnly: true, sx: { bgcolor: 'grey.50', borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Unique Student ID"
                  value={sub?.student_display_id || '—'}
                  InputProps={{ readOnly: true, sx: { bgcolor: 'grey.50', borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Billing Country"
                  value={sub?.country || '—'}
                  InputProps={{ readOnly: true, sx: { bgcolor: 'grey.50', borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Entry Date"
                  value={sub?.enrollment_date || '—'}
                  InputProps={{ readOnly: true, sx: { bgcolor: 'grey.50', borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <MenuBookIcon sx={{ color: 'primary.main', fontSize: 28 }} />
              <Typography variant="subtitle1" fontWeight="700">Course & Batch Details</Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" gutterBottom>Batch Type</Typography>
                <ToggleButtonGroup value={sub?.batch_type || 'one_on_one'} exclusive size="small" sx={{ mt: 0.5 }}>
                  <ToggleButton value="one_on_one">{BATCH_LABELS.one_on_one}</ToggleButton>
                  <ToggleButton value="group">{BATCH_LABELS.group}</ToggleButton>
                </ToggleButtonGroup>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Weekly Class Frequency"
                  value={sub ? `${sub.classes_per_week || 0} Classes / week` : '—'}
                  InputProps={{ readOnly: true, sx: { bgcolor: 'grey.50', borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Subscription Model"
                  value={sub ? PLAN_LABELS[sub.subscription_model] || sub.subscription_model : '—'}
                  InputProps={{ readOnly: true, sx: { bgcolor: 'grey.50', borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Subscription Start Date"
                  value={sub?.service_start_date || '—'}
                  InputProps={{ readOnly: true, sx: { bgcolor: 'grey.50', borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Standard Class Cost"
                  value={sub?.price_per_session_at_enrollment != null ? formatCurrency(sub.price_per_session_at_enrollment, sub.currency) + ' / class' : '—'}
                  InputProps={{ readOnly: true, sx: { bgcolor: 'grey.50', borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button variant="outlined" size="small" onClick={handleOpenEdit}>Edit subscription</Button>
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <LocalOfferIcon sx={{ color: 'primary.main', fontSize: 28 }} />
              <Typography variant="subtitle1" fontWeight="700">Discounts & Promotions</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                Apply sibling/referral/etc. discounts for this subscription.
              </Typography>
              <Button variant="outlined" size="small" onClick={handleOpenCreateDiscount} disabled={!id}>
                Add Discount
              </Button>
            </Box>

            {discountsQuery.isLoading ? (
              <Typography variant="body2" color="text.secondary">Loading discounts...</Typography>
            ) : discounts.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No discounts applied.</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Value</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>Notes</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {discounts.map((d) => (
                      <TableRow key={d.id} hover>
                        <TableCell>{d.discount_type}</TableCell>
                        <TableCell align="right">
                          {isTruthyBoolean(d.is_percentage ?? d.isPercentage) ? `${d.value}%` : formatCurrency(d.value, sub?.currency)}
                        </TableCell>
                        <TableCell>{d.duration}</TableCell>
                        <TableCell sx={{ maxWidth: 240 }}>
                          <Typography variant="body2" color="text.secondary" noWrap title={d.notes || ''}>
                            {d.notes || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Button size="small" onClick={() => handleOpenEditDiscount(d)} sx={{ mr: 1 }}>
                            Edit
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            onClick={() => {
                              setPendingEffectiveAction({
                                kind: 'discount_delete',
                                run: ({ applyFrom, fromInvoiceId }) => {
                                  deleteDiscountMutation.mutate({ discountId: d.id, applyFrom, fromInvoiceId });
                                },
                              });
                              setEffectiveMode('next');
                              setEffectiveInvoiceId('');
                              setEffectiveDialogOpen(true);
                            }}
                            disabled={deleteDiscountMutation.isLoading}
                          >
                            Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>

          <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <ExtensionIcon sx={{ color: 'primary.main', fontSize: 28 }} />
              <Typography variant="subtitle1" fontWeight="700">Add-ons</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Kits, materials, and other one-time charges. Unit prices come from{' '}
              <strong>Finance → Fees &amp; Pricing → Add-on pricing</strong> for this subscription&apos;s country and currency.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleOpenAddOn}
                disabled={!id || !!sub?.deleted_at || addOnCatalogQuery.isLoading}
              >
                Add add-on
              </Button>
            </Box>
            {addOnsQuery.isLoading ? (
              <Typography variant="body2" color="text.secondary">Loading add-ons...</Typography>
            ) : addOns.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No add-ons on this subscription.</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Unit price</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell align="right">Line total</TableCell>
                      <TableCell>Invoice</TableCell>
                      <TableCell>Added</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {addOns.map((a) => {
                      const unit = Number(a.unit_price) || 0;
                      const qty = Number(a.quantity) || 0;
                      const line = unit * qty;
                      const invLabel = a.invoice_id
                        ? (() => {
                            const inv = invoices.find((i) => i.id === a.invoice_id);
                            return inv ? formatInvoiceCycleLabel(inv) : a.invoice_id;
                          })()
                        : '—';
                      return (
                        <TableRow key={a.id} hover>
                          <TableCell>{addOnTypeLabel(a.add_on_type)}</TableCell>
                          <TableCell align="right">{formatCurrency(unit, sub?.currency)}</TableCell>
                          <TableCell align="right">{qty}</TableCell>
                          <TableCell align="right">{formatCurrency(line, sub?.currency)}</TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary" noWrap title={String(invLabel)}>
                              {invLabel}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {a.created_at
                              ? String(a.created_at).slice(0, 19).replace('T', ' ')
                              : '—'}
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              color="error"
                              onClick={() => {
                                setPendingEffectiveAction({
                                  kind: 'add_on_delete',
                                  run: ({ applyFrom, fromInvoiceId }) => {
                                    deleteAddOnMutation.mutate({ addOnId: a.id, applyFrom, fromInvoiceId });
                                  },
                                });
                                setEffectiveMode('next');
                                setEffectiveInvoiceId('');
                                setEffectiveDialogOpen(true);
                              }}
                              disabled={deleteAddOnMutation.isLoading || !!sub?.deleted_at}
                            >
                              Remove
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>

          <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <ReceiptIcon sx={{ color: 'primary.main', fontSize: 28 }} />
              <Typography variant="subtitle1" fontWeight="700">Pending Payments (Invoices)</Typography>
            </Box>

            {invoicesQuery.isLoading ? (
              <Typography variant="body2" color="text.secondary">Loading invoices...</Typography>
            ) : invoices.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No invoices found.</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Billing cycle</TableCell>
                      <TableCell>Payment Due</TableCell>
                      <TableCell align="right">Due</TableCell>
                      <TableCell align="right">Paid</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoices
                      .filter((inv) => ['open', 'partially_paid'].includes(String(inv.status)))
                      .map((inv) => (
                        <TableRow key={inv.id}>
                          <TableCell>{formatInvoiceCycleLabel(inv)}</TableCell>
                          <TableCell>{inv.payment_due_date || '—'}</TableCell>
                          <TableCell align="right">
                            {formatCurrency(
                              Math.max(0, (Number(inv.amount_due) || 0) - (Number(inv.amount_paid) || 0)),
                              sub?.currency
                            )}
                          </TableCell>
                          <TableCell align="right">{formatCurrency(inv.amount_paid, sub?.currency)}</TableCell>
                          <TableCell><StatusBadge status={String(inv.status)} /></TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>

          {canRecordPayment && (
            <RazorpayInvoicePanel
              subscriptionId={id}
              currency={sub?.currency}
              invoices={invoices}
              payments={payments}
              canManage={canRecordPayment}
            />
          )}

          <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <ShoppingCart sx={{ color: 'primary.main', fontSize: 28 }} />
              <Typography variant="subtitle1" fontWeight="700">Payment History</Typography>
            </Box>

            {paymentsQuery.isLoading ? (
              <Typography variant="body2" color="text.secondary">Loading payments...</Typography>
            ) : payments.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No payments recorded yet.</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>When</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Method</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Ref</TableCell>
                      <TableCell>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payments.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell>
                          {p.paid_at
                            ? String(p.paid_at).slice(0, 19).replace('T', ' ')
                            : (p.created_at ? String(p.created_at).slice(0, 19).replace('T', ' ') : '—')}
                        </TableCell>
                        <TableCell align="right">{formatCurrency(p.amount, p.currency || sub?.currency)}</TableCell>
                        <TableCell>{p.payment_method || '—'}</TableCell>
                        <TableCell>{String(p.status || '—')}</TableCell>
                        <TableCell>{p.manual_reference_id || p.gateway_reference_id || '—'}</TableCell>
                        <TableCell>{p.notes || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>

        {/* Right column – Billing Summary */}
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 3,
              boxShadow: 2,
              background: 'linear-gradient(135deg, #1e3a5f 0%, #2c5282 100%)',
              color: 'white',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <ReceiptIcon sx={{ fontSize: 28 }} />
              <Typography variant="subtitle1" fontWeight="700">Billing Summary</Typography>
            </Box>
            {isLoading ? (
              <Typography variant="body2">Loading...</Typography>
            ) : (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>Batch Type</Typography>
                  <Typography variant="body1" fontWeight="600">{sub ? BATCH_LABELS[sub.batch_type] || sub.batch_type || '—' : '—'}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>Total Classes Promised</Typography>
                  <Typography variant="body1" fontWeight="600">{computedTotalClasses ?? sub?.total_classes ?? '—'} Sessions</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>Base Rate</Typography>
                  <Typography variant="body1" fontWeight="600">{sub ? formatCurrency(sub.price_per_session_at_enrollment, sub.currency) + ' / class' : '—'}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>Duration</Typography>
                  <Typography variant="body1" fontWeight="600">{sub ? PLAN_LABELS[sub.subscription_model] || sub.subscription_model : '—'}</Typography>
                </Box>
                <Box sx={{ borderTop: 1, borderColor: 'rgba(255,255,255,0.3)', pt: 2, mt: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Subtotal (outstanding invoices)</Typography>
                    <Typography variant="body2">{formatCurrency(outstandingInvoiceGross, sub?.currency)}</Typography>
                  </Box>
                  {outstandingInvoiceRemaining !== outstandingInvoiceGross && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>Less: paid on open invoices</Typography>
                      <Typography variant="body2" sx={{ color: '#86efac' }}>
                        -{formatCurrency(Math.max(0, outstandingInvoiceGross - outstandingInvoiceRemaining), sub?.currency)}
                      </Typography>
                    </Box>
                  )}
                  {uninvoicedDiscountTotal > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>Discount (not yet on invoices)</Typography>
                      <Typography variant="body2" sx={{ color: '#86efac' }}>
                        -{formatCurrency(uninvoicedDiscountTotal, sub?.currency)}
                      </Typography>
                    </Box>
                  )}
                  {hasInvoicedDiscounts && (
                    <Typography variant="caption" sx={{ display: 'block', mb: 1, opacity: 0.75 }}>
                      Other discounts are already included in the invoice balances above.
                    </Typography>
                  )}
                  {uninvoicedAddOnTotal > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>Add-ons (not yet on invoices)</Typography>
                      <Typography variant="body2">+{formatCurrency(uninvoicedAddOnTotal, sub?.currency)}</Typography>
                    </Box>
                  )}
                  {hasInvoicedAddOns && uninvoicedAddOnTotal === 0 && (
                    <Typography variant="caption" sx={{ display: 'block', mb: 1, opacity: 0.75 }}>
                      Add-ons linked to invoices are included in the invoice balances above.
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5, alignItems: 'center' }}>
                    <Typography variant="subtitle1" fontWeight="700">Total Amount Due</Typography>
                    <Typography variant="h6" fontWeight="700">{formatCurrency(totalAmountDue, sub?.currency)}</Typography>
                  </Box>
                </Box>
                {canRecordPayment && (
                  <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={<ShoppingCart />}
                      onClick={handleOpenPayment}
                      sx={{
                        bgcolor: 'white',
                        color: 'primary.main',
                        '&:hover': { bgcolor: 'grey.100' },
                        borderRadius: 2,
                      }}
                    >
                      Collect Payment
                    </Button>
                    <Button variant="outlined" fullWidth sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)', borderRadius: 2 }}>
                      Download Estimate PDF
                    </Button>
                  </Box>
                )}
                <Typography variant="caption" sx={{ display: 'block', mt: 2, opacity: 0.7 }}>
                  SECURE TRANSACTION SYSTEM
                </Typography>
              </>
            )}
          </Paper>

          <Paper sx={{ p: 2, mt: 2, borderRadius: 3, boxShadow: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>CLASS AVAILABILITY</Typography>
            <Typography variant="h5" fontWeight="700">{sub?.classes_per_week ?? 0} Classes / week</Typography>
            <Typography variant="body2" color="text.secondary">Next due: {sub?.next_due_date || '—'}</Typography>
            <Box sx={{ mt: 1 }}><StatusBadge status={sub?.status} /></Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Record Payment Dialog */}
      <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record Payment</DialogTitle>
        <DialogContent dividers>
          {paymentForm.invoiceId && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              If this billing invoice also has an active Razorpay payment request, prefer waiting for the
              webhook or cancel the Razorpay invoice first to avoid double collection. Manual entry is still allowed.
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField label="Amount" type="number" value={paymentForm.amount} onChange={(e) => setPaymentForm((p) => ({ ...p, amount: e.target.value }))} fullWidth size="small" inputProps={{ min: 0, step: 0.01 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Currency" value={paymentForm.currency} onChange={(e) => setPaymentForm((p) => ({ ...p, currency: e.target.value.toUpperCase() }))} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Due Date" type="date" value={paymentForm.dueDate} onChange={(e) => setPaymentForm((p) => ({ ...p, dueDate: e.target.value }))} fullWidth size="small" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                label="Apply to invoice (optional)"
                value={paymentForm.invoiceId}
                onChange={(e) => setPaymentForm((p) => ({ ...p, invoiceId: e.target.value }))}
                fullWidth
                size="small"
                helperText="Leave blank to auto-apply across open invoices (earliest due first; matches Due Date when possible)."
              >
                <MenuItem value="">
                  <em>Auto (recommended)</em>
                </MenuItem>
                {invoices
                  .filter((inv) => ['open', 'partially_paid'].includes(String(inv.status)))
                  .map((inv) => {
                    const rem = Math.max(0, (Number(inv.amount_due) || 0) - (Number(inv.amount_paid) || 0));
                    return (
                      <MenuItem key={inv.id} value={inv.id}>
                        {formatInvoiceCycleLabel(inv)} — {formatCurrency(rem, sub?.currency)} remaining
                      </MenuItem>
                    );
                  })}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Paid At" type="datetime-local" value={paymentForm.paidAt} onChange={(e) => setPaymentForm((p) => ({ ...p, paidAt: e.target.value }))} fullWidth size="small" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Payment Method" value={paymentForm.paymentMethod} onChange={(e) => setPaymentForm((p) => ({ ...p, paymentMethod: e.target.value }))} fullWidth size="small">
                <MenuItem value="manual">Manual</MenuItem>
                <MenuItem value="card">Card</MenuItem>
                <MenuItem value="upi">UPI</MenuItem>
                <MenuItem value="gateway">Gateway</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Reference ID" value={paymentForm.manualReferenceId} onChange={(e) => setPaymentForm((p) => ({ ...p, manualReferenceId: e.target.value }))} fullWidth size="small" />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Notes" value={paymentForm.notes} onChange={(e) => setPaymentForm((p) => ({ ...p, notes: e.target.value }))} fullWidth size="small" multiline minRows={2} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmitPayment} disabled={recordPaymentMutation.isLoading}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Subscription Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit subscription</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField select fullWidth size="small" label="Status" value={editForm.status} onChange={(e) => setEditForm((p) => ({ ...p, status: e.target.value }))}>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="expired">Expired</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
                <MenuItem value="suspended">Suspended</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <Typography variant="body2" color="text.secondary" gutterBottom>Batch Type</Typography>
                <ToggleButtonGroup
                  value={editForm.batchType}
                  exclusive
                  size="small"
                  onChange={(_, v) => v && setEditForm((p) => ({ ...p, batchType: v }))}
                >
                  <ToggleButton value="one_on_one">1:1</ToggleButton>
                  <ToggleButton value="group">Group</ToggleButton>
                </ToggleButtonGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Service Start Date"
                type="date"
                value={editForm.serviceStartDate}
                onChange={(e) => setEditForm((p) => ({ ...p, serviceStartDate: e.target.value }))}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Enrollment Date"
                type="date"
                value={editForm.enrollmentDate}
                onChange={(e) => setEditForm((p) => ({ ...p, enrollmentDate: e.target.value }))}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <Typography variant="body2" color="text.secondary" gutterBottom>Plan</Typography>
                <RadioGroup value={editForm.subscriptionModel} onChange={(e) => setEditForm((p) => ({ ...p, subscriptionModel: e.target.value }))}>
                  {['monthly', 'quarterly', 'six_months', 'yearly'].map((m) => (
                    <FormControlLabel key={m} value={m} control={<Radio />} label={PLAN_LABELS[m]} />
                  ))}
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Classes per week" type="number" value={editForm.classesPerWeek} onChange={(e) => setEditForm((p) => ({ ...p, classesPerWeek: e.target.value }))} fullWidth size="small" inputProps={{ min: 1, max: 7 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Bonus classes" type="number" value={editForm.bonusClasses} onChange={(e) => setEditForm((p) => ({ ...p, bonusClasses: e.target.value }))} fullWidth size="small" inputProps={{ min: 0 }} />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Reason (optional)"
                value={editForm.reason}
                onChange={(e) => setEditForm((p) => ({ ...p, reason: e.target.value }))}
                fullWidth
                size="small"
                multiline
                minRows={2}
              />
            </Grid>
            {editForm.status === 'cancelled' && openInvoices.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  This subscription has {openInvoices.length} open invoice(s). Choose what should happen to them when you cancel the subscription.
                </Typography>
                <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 2, mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Cycle</TableCell>
                        <TableCell align="right">Due</TableCell>
                        <TableCell align="right">Paid</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {openInvoices.map((inv) => (
                        <TableRow key={inv.id}>
                          <TableCell>{formatInvoiceCycleLabel(inv)}</TableCell>
                          <TableCell align="right">{formatCurrency(inv.amount_due, sub?.currency)}</TableCell>
                          <TableCell align="right">{formatCurrency(inv.amount_paid, sub?.currency)}</TableCell>
                          <TableCell>{String(inv.status || '—')}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <FormControl component="fieldset">
                  <RadioGroup
                    value={editForm.openInvoiceAction || 'keep'}
                    onChange={(e) => setEditForm((p) => ({ ...p, openInvoiceAction: e.target.value }))}
                  >
                    <FormControlLabel
                      value="keep"
                      control={<Radio />}
                      label="Cancel subscription only and keep the open invoices unchanged"
                    />
                    <FormControlLabel
                      value="cancel"
                      control={<Radio />}
                      label="Cancel subscription and also cancel these open invoices"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmitEdit} disabled={updateSubscriptionMutation.isLoading}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Effective Date / Invoice Dialog */}
      <Dialog
        open={effectiveDialogOpen}
        onClose={() => setEffectiveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Apply subscription changes</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Choose when these changes should take effect.
          </Typography>

          <FormControl component="fieldset" sx={{ mt: 1 }}>
            <RadioGroup
              value={effectiveMode}
              onChange={(e) => setEffectiveMode(e.target.value)}
            >
              <FormControlLabel
                value="next"
                control={<Radio />}
                label="Apply from next bill (recommended)"
              />
              <FormControlLabel
                value="previous"
                control={<Radio />}
                label="Apply from a previous bill (select invoice)"
              />
            </RadioGroup>
          </FormControl>

          {effectiveMode === 'previous' && (
            <Box sx={{ mt: 2 }}>
              <TextField
                select
                label="Invoice"
                value={effectiveInvoiceId}
                onChange={(e) => setEffectiveInvoiceId(e.target.value)}
                fullWidth
                size="small"
                helperText="Showing only the last 2 invoices for this subscription."
              >
                {lastTwoInvoices.length === 0 ? (
                  <MenuItem value="">
                    <em>No invoices found</em>
                  </MenuItem>
                ) : (
                  lastTwoInvoices.map((inv) => (
                    <MenuItem key={inv.id} value={inv.id}>
                      {`${formatInvoiceCycleLabel(inv)} • ${String(inv.status || '').toUpperCase()}`}
                    </MenuItem>
                  ))
                )}
              </TextField>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Paid/partially paid invoices will not be modified. Any difference will be adjusted in the next unpaid invoice.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEffectiveDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleConfirmEffectiveUpdate}
            disabled={updateSubscriptionMutation.isLoading}
          >
            Confirm & Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add-on Dialog */}
      <Dialog open={addOnDialogOpen} onClose={() => setAddOnDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add add-on</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                select
                label="Add-on type"
                value={addOnForm.addOnType}
                onChange={(e) => setAddOnForm((p) => ({ ...p, addOnType: e.target.value }))}
                fullWidth
                size="small"
              >
                {addOnCatalog.map((a) => (
                  <MenuItem key={a.key} value={a.key}>
                    {a.label || a.key}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Quantity"
                type="number"
                value={addOnForm.quantity}
                onChange={(e) => setAddOnForm((p) => ({ ...p, quantity: e.target.value }))}
                fullWidth
                size="small"
                inputProps={{ min: 1, step: 1 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Link to invoice (optional)"
                value={addOnForm.invoiceId}
                onChange={(e) => setAddOnForm((p) => ({ ...p, invoiceId: e.target.value }))}
                fullWidth
                size="small"
                displayEmpty
              >
                <MenuItem value="">
                  <em>Not linked</em>
                </MenuItem>
                {invoices.map((inv) => (
                  <MenuItem key={inv.id} value={inv.id}>
                    {formatInvoiceCycleLabel(inv)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOnDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmitAddOn}
            disabled={createAddOnMutation.isLoading || !addOnForm.addOnType}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Subscription Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete subscription</DialogTitle>
        <DialogContent dividers>
          {openInvoices.length > 0 ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                This subscription has {openInvoices.length} open invoice(s). Review details below, then confirm if you still want to delete.
              </Typography>
              <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 2, mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Cycle</TableCell>
                      <TableCell align="right">Due</TableCell>
                      <TableCell align="right">Paid</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {openInvoices.map((inv) => (
                      <TableRow key={inv.id}>
                        <TableCell>{formatInvoiceCycleLabel(inv)}</TableCell>
                        <TableCell align="right">{formatCurrency(inv.amount_due, sub?.currency)}</TableCell>
                        <TableCell align="right">{formatCurrency(inv.amount_paid, sub?.currency)}</TableCell>
                        <TableCell>{String(inv.status || '—')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <FormControl component="fieldset">
                <RadioGroup
                  value={deleteOpenInvoiceAction}
                  onChange={(e) => setDeleteOpenInvoiceAction(e.target.value)}
                >
                  <FormControlLabel
                    value="keep"
                    control={<Radio />}
                    label="Delete subscription only and keep the open invoices unchanged"
                  />
                  <FormControlLabel
                    value="cancel"
                    control={<Radio />}
                    label="Delete subscription and also cancel these open invoices"
                  />
                </RadioGroup>
              </FormControl>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This will soft delete the subscription (kept for audit/reporting) and set status to cancelled.
            </Typography>
          )}

          <TextField
            label="Reason (optional)"
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            fullWidth
            size="small"
            multiline
            minRows={2}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={deleteSubscriptionMutation.isLoading}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Change History
        </Typography>
        {changeHistoryQuery.isLoading ? (
          <Typography variant="body2" color="text.secondary">Loading history...</Typography>
        ) : changeHistory.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No changes recorded.</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Field</TableCell>
                  <TableCell>Old</TableCell>
                  <TableCell>New</TableCell>
                  <TableCell>By</TableCell>
                  <TableCell>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {changeHistory.map((h) => (
                  <TableRow key={h.id}>
                    <TableCell>{new Date(h.changed_at).toLocaleString()}</TableCell>
                    <TableCell>{h.field_name}</TableCell>
                    <TableCell>{h.old_value ?? '—'}</TableCell>
                    <TableCell>{h.new_value ?? '—'}</TableCell>
                    <TableCell>{h.changed_by_email || '—'}</TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>
                      <Typography variant="body2" color="text.secondary" noWrap title={h.reason || ''}>
                        {h.reason || '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Add/Edit Discount Dialog */}
      <Dialog open={discountDialogOpen} onClose={() => { setDiscountDialogOpen(false); setEditingDiscount(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>{editingDiscount ? 'Edit Discount' : 'Add Discount'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Discount Type"
                value={discountForm.discountType}
                onChange={(e) => handleDiscountChange('discountType', e.target.value)}
                fullWidth
                size="small"
              >
                {discountCatalogQuery.isLoading ? (
                  <MenuItem value={discountForm.discountType}>Loading...</MenuItem>
                ) : activeDiscountTypes.length === 0 ? (
                  <MenuItem value={discountForm.discountType}>No active discount types</MenuItem>
                ) : (
                  activeDiscountTypes.map((t) => (
                    <MenuItem key={t.type} value={t.type}>
                      {t.label || t.type}
                    </MenuItem>
                  ))
                )}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Value"
                type="number"
                value={discountForm.value}
                onChange={(e) => handleDiscountChange('value', e.target.value)}
                fullWidth
                size="small"
                disabled={isSelectedDiscountTypeLocked}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Is Percentage?"
                value={discountForm.isPercentage ? 'yes' : 'no'}
                onChange={(e) => handleDiscountChange('isPercentage', e.target.value === 'yes')}
                fullWidth
                size="small"
                disabled={isSelectedDiscountTypeLocked}
              >
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Duration"
                value={discountForm.duration}
                onChange={(e) => handleDiscountChange('duration', e.target.value)}
                fullWidth
                size="small"
              >
                <MenuItem value="one_time">One-time</MenuItem>
                <MenuItem value="permanent">Permanent</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notes"
                value={discountForm.notes}
                onChange={(e) => handleDiscountChange('notes', e.target.value)}
                fullWidth
                size="small"
                multiline
                minRows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDiscountDialogOpen(false); setEditingDiscount(null); }}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmitDiscount} disabled={discountMutation.isLoading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubscriptionDetails;
